import { useMemo } from "react";

import { useRefract } from "refract-rxjs";

import { type MetricField } from "../../gql-generated/types";
import { type ApolloResult } from "../../shared/models";
import { makeUTCStartEndTimestamp } from "../converters";
import {
    type LensOrganicStatsTotalEffectInput,
    type LensOrganicStatsTotalEffectOutput,
    makeLensOrganicStatsTotalEffect,
} from "../effects";
import { GranularityInDays, type LensGranularity, metricFieldsTotal } from "../models";

import { useOrganicStatsQuery } from "./useOrganicStatsQuery";
import { useTimeRange } from "./useTimeRange";

const defaultEffect = makeLensOrganicStatsTotalEffect(metricFieldsTotal);

export interface UseOrganicStatsTotalParams {
    metricFields?: MetricField[];
    defaultRange?: LensGranularity;
}

export const useOrganicStatsTotal = (
    params?: UseOrganicStatsTotalParams,
): ApolloResult<LensOrganicStatsTotalEffectOutput> => {
    const metricFields = params?.metricFields ?? metricFieldsTotal;
    const { isAllTimeSelected, selectedTimeRange } = useTimeRange({ defaultRange: params?.defaultRange });

    const effect = useMemo(() => {
        if (params?.metricFields === undefined) {
            return defaultEffect;
        }
        return makeLensOrganicStatsTotalEffect(params.metricFields);
    }, [params?.metricFields]);

    const rangeDays = GranularityInDays[selectedTimeRange] ?? 0;

    const { data, error, loading } = useOrganicStatsQuery({
        isAllTimeSelected,
        isTotal: true,
        metricFields,
        startEndTimestamp: makeUTCStartEndTimestamp(rangeDays),
    });

    // The previous-period window starts at today - 2*rangeDays. Organic-stats
    // upstream rejects start/end times older than 90 days (SRS_VALIDATION-35),
    // so for ranges over 45 days the previous query always fails. Skip it and
    // let the diff render without comparison rather than throwing INTERNAL.
    const PREVIOUS_PERIOD_MAX_RANGE_DAYS = 45;
    const skipPrevious = isAllTimeSelected || rangeDays > PREVIOUS_PERIOD_MAX_RANGE_DAYS;

    const {
        data: dataPrevious,
        error: errorPrevious,
        loading: loadingPrevious,
    } = useOrganicStatsQuery({
        isAllTimeSelected,
        isTotal: true,
        metricFields,
        startEndTimestamp: makeUTCStartEndTimestamp(rangeDays, -1 * rangeDays),
        skip: skipPrevious,
    });

    const metricsData = useRefract<LensOrganicStatsTotalEffectInput, LensOrganicStatsTotalEffectOutput>(effect, {
        data,
        loading,
        dataPrevious,
        loadingPrevious,
    });

    return { ...metricsData, loading: loading || loadingPrevious, error: error ?? errorPrevious };
};
